import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { BrowserAuthError, InteractionType } from '@azure/msal-browser';
import { TranslateService } from '@ngx-translate/core';
import { msalConfig } from '../../environments/environment';
import { CONSTANTS } from '../Constants/Constants';
import {
  BusinessTypes,
  CollaborationHistoryData,
  CollaborationHubData,
  CostReviewDatabaseRecord,
  CostReviewSummary,
  CostReviewStatus,
  EstimateStatus,
  MinimumProjectStandardMarginRequestBase,
  SQWebSystem,
} from '../models/dtos';
import { LoadingSpinnerService } from './common/loading-spinner.service';
import { firstValueFrom } from 'rxjs';
import { CommonDataService } from './common-data.service';
import { UserProfileService } from './common/user-profile.service';

import { CommonMetaDataService } from './common-meta-data.service';
import { CULTURE_ID, FireEstimateAnalysisType } from '../models/enum';
import { TaskBasedLaborSettings } from '../models/dtos/Classes/TaskBasedLaborSettings';


@Injectable({
  providedIn: 'root'
})
export class CommonFunctionsService {

  // This is a helper class, DO NOT inject business services here.
  // Use this class as a utility to perform common functions that are not dependent on any business service.
  constructor(
    private readonly msalService: MsalService,
    private readonly router: Router,
    private readonly loadingSpinnerService: LoadingSpinnerService,
    private readonly translate: TranslateService,
    private readonly commonDataService: CommonDataService,
    @Inject(MSAL_GUARD_CONFIG) private readonly msalGuardConfig: MsalGuardConfiguration,
    private readonly userProfileService: UserProfileService,
    private readonly commonMetaDataService: CommonMetaDataService
  ) {}

  private isLoginInProgress = false;
  private isSessionExpiryHandled = false;

  static readonly SESSION_EXPIRED_FLAG = 'sessionExpiredDueToInactivity';

  /** True when MSAL's hidden-iframe silent renewal timed out (idle session with no SSO cookie). */
  isSilentRenewalTimeout(error: unknown): boolean {
    return error instanceof BrowserAuthError && error.errorCode === 'monitor_window_timeout';
  }

  /**
   * Wipes tokens/cached state locally and sends the user to /login with an inactivity message.
   * Local-only: the Entra session is already gone, so no end_session redirect is made.
   */
  async handleSessionExpiredDueToInactivity(): Promise<void> {
    // Parallel API calls fail together; handle only once.
    if (this.isSessionExpiryHandled) {
      return;
    }
    this.isSessionExpiryHandled = true;

    this.clearApplicationSession();
    try {
      this.msalService.instance.setActiveAccount(null);
      await this.msalService.instance.clearCache();
    } catch {
      // Best-effort cleanup.
    }

    try {
      sessionStorage.setItem(CommonFunctionsService.SESSION_EXPIRED_FLAG, 'true');
    } catch {
      // Ignore storage access errors.
    }

    this.isLoginInProgress = false;
    await this.router.navigate(['/login']);
    this.isSessionExpiryHandled = false;
  }

  login(): void {
    // Prevent duplicate invocations while a login interaction is already in flight.
    if (this.isLoginInProgress) {
      return;
    }

    this.isLoginInProgress = true;

    // Do NOT await firstValueFrom(loginRedirect()): loginRedirect triggers a full
    // page navigation, so its Promise never resolves in the current window and
    // any await would hang forever (leaving the login button permanently disabled).
    // Subscribe fire-and-forget so errors surface but flow control is not blocked.
    this.msalService.loginRedirect().subscribe({
      error: (error: unknown) => {
        // Handle the classic BrowserAuthError: interaction_in_progress. This
        // happens when MSAL's sessionStorage flag was left set by an aborted
        // redirect. Clear it and retry once.
        if (
          error instanceof BrowserAuthError &&
          error.errorCode === 'interaction_in_progress'
        ) {
          try {
            sessionStorage.removeItem('msal.interaction.status');
          } catch {
            // Ignore sessionStorage access errors (e.g. privacy modes).
          }
          this.msalService.loginRedirect().subscribe({
            error: (retryError: unknown) => {
              console.error('MSAL loginRedirect retry failed:', retryError);
              this.isLoginInProgress = false;
            }
          });
          return;
        }

        console.error('MSAL loginRedirect failed:', error);
        this.isLoginInProgress = false;
      }
    });
  }

  async logout(): Promise<void> {
    // Capture the currently signed-in account BEFORE clearing anything.
    // MSAL uses this to build the id_token_hint on the end_session_endpoint call,
    // so Entra ID knows which session to terminate without prompting the user
    // to pick an account.
    const account =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0] ??
      null;

    // Clear any application-owned tokens/state we manage ourselves.
    // NOTE: MSAL will clear its own cache (access token, ID token, refresh token,
    // account) automatically as part of the logout flow — do NOT call
    // instance.clearCache() before logoutRedirect(), because that removes the
    // account info MSAL needs to construct id_token_hint.
    this.clearApplicationSession();

    try {
      if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
        // Popup flow: end the Entra ID session in a popup, then redirect the
        // main window to /login. This ends the SSO session server-side and
        // invalidates the refresh token.
        await firstValueFrom(
          this.msalService.logoutPopup({
            account,
            mainWindowRedirectUri: '/login'
          })
        );
        this.msalService.instance.setActiveAccount(null);
        this.router.navigate(['/login']);
      } else {
        // Redirect flow: navigates the browser to Entra ID's end_session_endpoint,
        // which invalidates the server-side SSO session + refresh token and then
        // redirects back to postLogoutRedirectUri (configured as /login).
        // Because the browser is leaving the page, code after this line does not
        // execute — do not call router.navigate here.
        this.msalService.instance.setActiveAccount(null);
        await firstValueFrom(
          this.msalService.logoutRedirect({
            account,
            postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri
          })
        );
      }
    } catch (error) {
      console.error('MSAL logout failed:', error);
      // Fallback: force-clear the local MSAL cache so the user is at least
      // signed out client-side even if the server round-trip failed.
      try {
        await this.msalService.instance.clearCache();
      } catch {
        // Best-effort cleanup; ignore secondary failures.
      }
      this.router.navigate(['/login']);
    }
  }

  /**
   * Clears app-owned session state on logout.
   *
   * IMPORTANT — token invalidation semantics:
   *  - MSAL's cache (access token, ID token, refresh token, account) is cleared
   *    by MSAL itself as part of logoutRedirect / logoutPopup. Calling
   *    clearCache() ourselves BEFORE that call would strip the id_token_hint
   *    and cause Entra ID to prompt for an account.
   *  - The end_session_endpoint call performed by logoutRedirect terminates
   *    the SSO session and refresh token server-side. After this, MSAL cannot
   *    silently acquire new tokens for this user without a fresh interactive
   *    login.
   *  - Access tokens and ID tokens are stateless signed JWTs. They CANNOT be
   *    revoked before their `exp` claim from the browser — any copies already
   *    handed to APIs remain cryptographically valid until they expire. If you
   *    need immediate server-side revocation of all sessions for a user, call
   *    Microsoft Graph `POST /users/{id}/revokeSignInSessions` from a trusted
   *    backend. The frontend logout flow here is the standard OIDC RP-initiated
   *    logout and is the correct client-side behavior.
   */
  private clearApplicationSession(): void {
    // Remove app-specific token copy (used by backend calls that don't go
    // through MSAL's acquireTokenSilent path).
    try {
      localStorage.removeItem('idToken');
    } catch {
      // Ignore storage access errors (e.g. privacy mode).
    }

    // Drop in-memory user profile so a stale identity is not shown after
    // logout / before a fresh login completes.
    this.userProfileService.userInfo = null!;
  }

  startSpinner(spinnerName: string = '') {
    this.loadingSpinnerService.show(spinnerName || CONSTANTS.DASHBOARD_SPINNER);
  }

  stopSpinner(spinnerName: string = '') {
    this.loadingSpinnerService.hide(spinnerName || CONSTANTS.DASHBOARD_SPINNER);
  }

  convertToPercentage(percentage: number): string {
    try {
      let result = CONSTANTS.ZERO_PERCENT;
      if (percentage !== null && percentage !== undefined) {
        // Format the percentage with 2 decimal places
        result = percentage.toFixed(2) + CONSTANTS.PERCENT;
      }
      return result;
    } catch (ex) {
      console.error('ConvertToPercentage', ex);
      return '';
    }
  }

  getBusinessName(id: string): string {
    switch (id) {
      case CONSTANTS.BUSINESS_AUTOMATION:
        return this.translate.instant('SOL_BUSINESS.AUTOMATION');
      case CONSTANTS.BUSINESS_FIRE:
        return this.translate.instant('SOL_BUSINESS.FIRE');
      case CONSTANTS.BUSINESS_SECURITY:
        return this.translate.instant('SOL_BUSINESS.SECURITY');
      case CONSTANTS.BUSINESS_ELECTRICAL:
        return this.translate.instant('SOL_BUSINESS.ELECTRICAL');
      case CONSTANTS.BUSINESS_MECHANICAL:
        return this.translate.instant('SOL_BUSINESS.MECHANICAL');
      default:
        return '';
    }
  }

  // This function handles the New and old Electrical business logic (Backward compatibility - as electrical was part of automation)
  getBusiness() {
    const business = this.commonDataService.getBusiness();
    if (business === CONSTANTS.BUSINESS_ELECTRICAL) {
      const reviewObject = this.commonDataService.getCostReviewData()?.reviewObject;
      if (reviewObject) {
        return business;
      }
      if ('costReviewScopeOfWork' in reviewObject) {
        return CONSTANTS.BUSINESS_ELECTRICAL;
      }
      return CONSTANTS.BUSINESS_AUTOMATION;
    } else {
      return business;
    }
  }

  getEstimateStatusName(id: number): string {
    switch (id) {
      case EstimateStatus.Active:
        return this.translate.instant('ACTIVE');
      case EstimateStatus.InActive:
        return this.translate.instant('INACTIVE');
      case EstimateStatus.Submitted:
        return this.translate.instant('SUBMITTED');
      default:
        return CONSTANTS.EMPTY_STRING;
    }
  }

  getZeeOfficeIds(): number[] {
    return this.parseZoneOfficeIds(this.commonMetaDataService.getMetaData()?.allZoneOfficeIds?.ee);
  }

  getZocOfficeIds(): number[] {
    return this.parseZoneOfficeIds(this.commonMetaDataService.getMetaData()?.allZoneOfficeIds?.oc);
  }

  getZineOfficeIds(): number[] {
    return this.parseZoneOfficeIds(this.commonMetaDataService.getMetaData()?.allZoneOfficeIds?.zine);
  }

  isZineOffice(salesOfficeId: number): boolean {
    return this.getZineOfficeIds().includes(salesOfficeId);
  }

  isEngOffice(salesOfficeId: number): boolean {
    return this.getZeeOfficeIds().includes(salesOfficeId) || this.getZocOfficeIds().includes(salesOfficeId);
  }

  isStandardOffice(salesOfficeId: number): boolean {
    return !this.isZineOffice(salesOfficeId) && !this.isEngOffice(salesOfficeId);
  }

  private parseZoneOfficeIds(ids: string | null | undefined): number[] {
    if (!ids) return [];
    return ids.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
  }

  getBusinessType(businessType: string): BusinessTypes {
    switch (businessType) {
      case 'A':
        return BusinessTypes.AUTOMATION;
      case 'F':
        return BusinessTypes.FIRE;
      case 'S':
        return BusinessTypes.SECURITY;
      case 'E':
        return BusinessTypes.ELECTRICAL;
      case 'M':
        return BusinessTypes.MECHANICAL;
      case 'SA':
        return BusinessTypes.SERVICE_AGREEMENT;
      default:
        return null;
    }
  }

  getBase64StringFromBytes(bytes: Uint8Array): Promise<string> {
    return new Promise((resolve, reject) => {
      const blob = new Blob([bytes.buffer as ArrayBuffer]);
      const reader = new FileReader();

      reader.onloadend = () => {
        const result = reader.result as string;
        // Strip data URL prefix (data:application/pdf;base64,...)
        const base64 = result.substring(result.indexOf(",") + 1);
        resolve(base64);
      };

      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  getCostReviewChecklistNameBasedOnBusiness() : string {
      switch (this.getBusiness()) {
        case CONSTANTS.BUSINESS_FIRE:
          return 'CHECKLIST_FIRE';
        case CONSTANTS.BUSINESS_ELECTRICAL:
          return 'CHECKLIST_ELECTRICAL';
        case CONSTANTS.BUSINESS_AUTOMATION:
          return 'CHECKLIST_AUTOMATION';
        case CONSTANTS.BUSINESS_SECURITY:
          return 'CHECKLIST_SECURITY_REPORT';
        case CONSTANTS.BUSINESS_MECHANICAL:
          return 'CHECKLIST_MECHANICAL';
        default:
          return 'CHECKLIST_AUTOMATION';
      }
  }


  getCultureId(cultureString: string): number {
    switch (cultureString) {
      case 'en-US':
        return CONSTANTS.ONE;
      case 'fr-CA':
        return CONSTANTS.TWO;
      case 'en-CA':
        return CONSTANTS.THREE;
      default:
        return CONSTANTS.ONE;
    }
  }

  // Get Cost review current status
  getCostReviewStatusName(costReviewData: CostReviewDatabaseRecord): string {
    const statusId = costReviewData?.status ?? 0;
    const statusMap: Record<number, string> = {
      [CostReviewStatus.None]: 'None',
      [CostReviewStatus.Pending]: 'Pending',
      [CostReviewStatus.Approved]: 'Approved',
      [CostReviewStatus.Denied]: 'Denied',
      [CostReviewStatus.NotStarted]: 'Not Started',
      [CostReviewStatus.InProcess]: 'In Process'
    };
    return statusMap[statusId] || 'Unknown';
  }

  // Get the cost review updated record from the change in estimate tab before save
  // Updates the cost review record to reflect whether the cost review related properties change before saving
  getCostReviewUpdatedRecord(): void {
    const costReviewRecord = this.commonDataService?.getCostReviewData();
    if (costReviewRecord) {
      costReviewRecord.isSellPriceChange = costReviewRecord?.reviewObject?.isAutoUnlockBySystem ?? false;
    }
  }

  /**
 * Case-insensitive lookup for a key in the general configurations dictionary.
 * The backend may serialize dictionary keys as camelCase (e.g. via
 * CamelCasePropertyNamesContractResolver or DictionaryKeyPolicy.CamelCase),
 * so a direct bracket access with the PascalCase key can return undefined.
 */
  getFireJobDetailsProductTypes(): string[] {
    const key = this.commonDataService.getEstimateCultureId() === 1
      ? 'FireJobDetailsProductType'
      : 'FireJobDetailsProductTypeCA';
    return (this.getGeneralConfigValue(key) ?? '')
      .split(',')
      .map(value => value.trim())
      .filter(value => value.length > 0);
  }

  public getGeneralConfigValue(key: string): string | undefined {
    const config = this.commonMetaDataService.getGeneralConfigurations();
    if (!config) {
      return undefined;
    }
    if (config[key] !== undefined) {
      return config[key];
    }
    const lowerKey = key.toLowerCase();
    const matchedKey = Object.keys(config).find(k => k.toLowerCase() === lowerKey);
    return matchedKey ? config[matchedKey] : undefined;
  }

  toNumberOrNull(value: unknown): number | null {
    if (value == null || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value === 'string') {
      const numericText = value.replace(/[^0-9.-]/g, '');
      if (!numericText) {
        return null;
      }

      const parsed = Number(numericText);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  buildMinimumProjectStandardMarginRequest(
    cultureId: number,
    verticalMarketId: number,
    segmentId: number,
    subSegmentId: number,
    businessUnitId: number,
    salesType: number,
    systemType: number
  ): MinimumProjectStandardMarginRequestBase {
    return {
      cultureId,
      verticalMarketId,
      segmentId,
      subSegmentId,
      systemOrSalesTypeId: this.getSystemOrSalesTypeIdForMinimumMargin(businessUnitId, cultureId, salesType, systemType),
    };
  }

  getSystemOrSalesTypeIdForMinimumMargin(
    businessUnitId: number,
    cultureId: number,
    salesType: number,
    systemType: number
  ): number {
    return this.isSalesTypeApplicableForMinimumMargin(businessUnitId, cultureId)
      ? salesType
      : systemType;
  }

  isSalesTypeApplicableForMinimumMargin(businessUnitId: number, cultureId: number): boolean {
    return businessUnitId === CONSTANTS.SIXTEEN && cultureId !== CULTURE_ID.EN_US; // Business Unit - Solutions
  }

  isEstimateOpen(): boolean {
    return (this.commonDataService.getEstimateId() !== CONSTANTS.DEFAULT_ESTIMATE_ID);
  }

  /**
     * Resolves the IndexedDB/collection lookup key for a system.
     * For typical-of systems, returns the master's itemUniqueId via the helper dictionary.
     * For normal systems, returns the system's own itemUniqueId.
     */
  private getTypicalOfHelper(system: SQWebSystem) {
    const typicalId = system.systemTypicalOfHelperUniqueId;
    if (typicalId) {
      const typicalOfHelpers = this.commonDataService.getTypicalOfHelpers();
      return typicalOfHelpers[typicalId];
    }
    return undefined;
  }

    getCollectionHolderId(system: SQWebSystem): string {
      const helper = this.getTypicalOfHelper(system);
      if (helper?.typicalOfCollectionHolderId) {
        return helper.typicalOfCollectionHolderId;
      }
      return system.itemUniqueId;
    }
  
    getSystemTypicalOfCount(system: SQWebSystem): number {
      const helper = this.getTypicalOfHelper(system);
      return helper?.typicalOfSystems?.length ?? CONSTANTS.ONE;
    }
  
    getSystemTypicalOfCountInArea(system: SQWebSystem, areaId: string): number {
      const helper = this.getTypicalOfHelper(system);
      const count = helper?.typicalOfSystems?.filter(s => s.parentUniqueId === areaId).length;
      return count ?? CONSTANTS.ONE;
      }

  isUserGOC(){
    return this.commonMetaDataService.getUserProfile()?.zoneId === CONSTANTS.GOC_ZONE_ID;
  }

  isQuotedServiceEstimate(): boolean {
    const estimateSubSegmentId = this.commonDataService.getEstimateData()?.subSegmentID;
    const subSegmentType = this.commonMetaDataService.getSubSegmentDetails(estimateSubSegmentId)?.estimateSubSegmentType;

    return subSegmentType === CONSTANTS.THREE;
  }

  isSdiApplicable(): boolean {
    const applicationCultureId = this.commonDataService.getPreferenceData()?.preferences?.cultureString;
    return (applicationCultureId === CONSTANTS.LANGUAGE_EN_US) && (!this.isQuotedServiceEstimate());
  }

  isBPSEnergyServicesSegmentId(segment: number): boolean {
    return segment == CONSTANTS.NINETEEN || // For BPS Energy Services (US)
      segment == CONSTANTS.TWENTY; // For BPS Energy Services (CA)
  }

  isBPSEnergyServicesIoTSegmentID(segment: number): boolean {
    return segment == CONSTANTS.FORTY || // For BPS Energy Services IoT (US)
      segment == CONSTANTS.FORTY_ONE; // For BPS Energy Services IoT (CA)
  }

  hasJobDetailsErrors(taskBasedLaborSettings: TaskBasedLaborSettings) {
    if (!taskBasedLaborSettings) {
      return false;
    }
    const noOfferingSelected = this.getSelectedOfferings(taskBasedLaborSettings).length === 0;

    const installationTypeInvalid = taskBasedLaborSettings.selectedProductTypeValue === null || taskBasedLaborSettings.selectedProductTypeValue === undefined || taskBasedLaborSettings.selectedProductTypeValue === CONSTANTS.NONE;

    const siteSizeInvalid = (taskBasedLaborSettings.isFireAlarmOfferingSelected ||
      taskBasedLaborSettings.isFireSprinklerOfferingSelected ||
      taskBasedLaborSettings.isGasSystemOfferingSelected ||
      taskBasedLaborSettings.isERCESOfferingSelected) && taskBasedLaborSettings.fireJobDetailsSiteSize <= CONSTANTS.ZERO;

    const gasSystemFieldsInvalid = taskBasedLaborSettings.isGasSystemOfferingSelected && (taskBasedLaborSettings.fireJobDetailsAgentWeight <= CONSTANTS.ZERO || taskBasedLaborSettings.fireJobDetailsCleanAgentVolSpace <= CONSTANTS.ZERO);
    const ercesFieldInvalid = taskBasedLaborSettings.isERCESOfferingSelected && (taskBasedLaborSettings.fireJobDetailsAntennaQty <= CONSTANTS.ZERO);

    return noOfferingSelected || installationTypeInvalid || siteSizeInvalid
      || gasSystemFieldsInvalid || ercesFieldInvalid;
  }

  getSelectedOfferings(taskBasedLaborSettings: TaskBasedLaborSettings) {
    const selectedOfferings = [];
    taskBasedLaborSettings.isFireAlarmOfferingSelected && selectedOfferings.push(FireEstimateAnalysisType.FIRE_ALARM);
    taskBasedLaborSettings.isFireSprinklerOfferingSelected && selectedOfferings.push(FireEstimateAnalysisType.FIRE_SPRINKLER);
    taskBasedLaborSettings.isGasSystemOfferingSelected && selectedOfferings.push(FireEstimateAnalysisType.GASEOUS_SYSTEM);
    taskBasedLaborSettings.isERCESOfferingSelected && selectedOfferings.push(FireEstimateAnalysisType.ERCES);
    return selectedOfferings;
  }

  mapJobDetailsToSummary(summary: CostReviewSummary, settings: TaskBasedLaborSettings): void {
    summary.hasJobDetailsValidationError = this.hasJobDetailsErrors(settings);
    summary.jobDetailsSelectedProductType = this.getSelectedOfferings(settings)
      .map(offering => this.translate.instant(offering))
      .join(', ');
    summary.jobDetailsInstallationType = settings.selectedProductTypeValue ?? CONSTANTS.EMPTY_STRING;
    summary.jobDetailsSiteSize = settings.fireJobDetailsSiteSize ?? CONSTANTS.ZERO;
    summary.jobDetailsAntennaQty = settings.fireJobDetailsAntennaQty ?? CONSTANTS.ZERO;
    summary.jobDetailsAgentWeight = settings.fireJobDetailsAgentWeight ?? CONSTANTS.ZERO;
    summary.jobDetailsCleanAgentVolSpace = settings.fireJobDetailsCleanAgentVolSpace ?? CONSTANTS.ZERO;
  }

  calculateDurationDays(startDate: Date, endDate: Date): number {
    const dayInMilliseconds = 24 * 60 * 60 * 1000;
    const start = new Date(startDate).setHours(0, 0, 0, 0);
    const end = new Date(endDate).setHours(0, 0, 0, 0);
    return Math.max(CONSTANTS.ZERO, Math.round((end - start) / dayInMilliseconds));
  }

  toUtcDate(selectedDate: Date | null): Date | null {
    if (!selectedDate) {
      return null;
    }
    return new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()));
  }

  getSubmittalPackageFromPreferences(): string {
      const preferences = this.commonDataService.getPreferenceData()?.preferences;
      const businessType = this.getBusinessType(this.commonDataService.getBusiness());
      if (!preferences || !businessType) {
        return '';
      }
      let packageIds = '';
      switch (businessType) {
        case BusinessTypes.AUTOMATION:
          packageIds = preferences.automationSubmittalId ?? '';
          break;
        case BusinessTypes.FIRE:
          packageIds = preferences.fireSubmittalId ?? '';
          break;
        case BusinessTypes.SECURITY:
          packageIds = preferences.securitySubmittalId ?? '';
          break;
        case BusinessTypes.ELECTRICAL:
          packageIds = preferences.electricalSubmittalId ?? '';
          break;
        case BusinessTypes.MECHANICAL:
          packageIds = preferences.mechanicalSubmittalId ?? '';
          break;
        default:
          return '';
      }
      const ids = packageIds.split(',').map(id => id.trim());
      const cultureId = this.commonDataService.getEstimateCultureId() ?? CONSTANTS.ONE;
      return ids.length === CONSTANTS.THREE ? (ids[cultureId - CONSTANTS.ONE] ?? '') : (ids[CONSTANTS.ZERO] ?? '');
    }

    updateCollaborationHubData(collaborationHubData: CollaborationHubData) {      
      if (!collaborationHubData) {
        return;
      }
      const mappedHistory = (collaborationHubData?.collaborationHistory ?? []).map((item): CollaborationHistoryData => {
        return {
          collaboratorName: item.collaboratorName,
          modifiedDate: item.modifiedDate,
          historyId: item.operation,
          modificationNotes: item.modificationNotes,
          isCurrentEditor: false,
          collaboratorEmail: ''
        };
      });

      const currentEditor = collaborationHubData?.currentEditor;
      const editorEmail = currentEditor?.email?.toLowerCase() ?? '';
      const hasCurrentEditor = editorEmail.length > 0;
      if (hasCurrentEditor) {
        mappedHistory.unshift({
          collaboratorName: currentEditor.name,
          modifiedDate: collaborationHubData.checkOutTime,
          historyId: null,
          modificationNotes: '',
          isCurrentEditor: true,
          collaboratorEmail: currentEditor.email
        });
      }
      this.commonDataService.setCollaborationHistory(mappedHistory);
    }

    collaborationTimestamp(value: Date | string | null | undefined): number {
      if (value instanceof Date) {
        return value.getTime();
      }
      if (!value || value.startsWith('0001-')) {
        return Number.NaN;
      }
      return new Date(/(?:Z|[+-]\d{2}:\d{2})$/i.test(value) ? value : `${value}Z`).getTime();
    }
  
}